import React, { useEffect, useState } from "react";
import axios from "axios";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import DocumentChat from "./components/DocumentChat";
import ChatButton from "./components/ChatButton";
import { useTheme } from "./context/ThemeContext";
import TileContainer from "./components/TileContainer";
import KpiSelectors from "./components/KpiSelectors";
import UnifiedBenchmarkFlow from "./components/UnifiedBenchmarkFlow";
import UsageTiles from "./components/UsageTiles";
import CompareCompany from "./components/CompareCompany";
import ContributeDataTiles from "./components/ContributeDataTiles";
import ContributeRawDataPage from "./components/ContributeRawDataPage";
import ContributeDataPage from "./components/ContributeDataPage";
import KpiUpload from "./components/KpiUpload";
import UserMenu from "./components/UserMenu";
import BenchMarkingSummary from "./components/BenchMarkingSummary";
import CompanyBenchMarkingSummary from "./components/CompanyBenchMarkingSummary";
import Help from "./components/Help";
import Feedback from "./components/Feedback";
import LoginPage from "./components/LoginPage";
import UserManagement from "./components/UserManagement";
import RoleManagement from "./components/RoleManagement";
import AccountManagement from "./components/AccountManagement";
import AccountsList from "./components/AccountsList";

type TileSelection = {
  id?: number;
};

type AuthUser = {
  id?: number | string;
  email?: string;
  role_name?: string | null;
};

type AuthState = {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isAdmin: boolean;
};

type SelectionChangePayload = Record<string, unknown>;

const RESOLVED_API_BASE_URL = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
const ACCOUNT_OPTIONS_API_BASE_URL =
  `${RESOLVED_API_BASE_URL}/api/user-management/account-aligned-options`;
const SELECTED_ACCOUNTS_KEY = "selectedAccountAlignedList";

const ACCESS_DENIED: JSX.Element = (
  <div className="min-h-[60vh] flex items-center justify-center px-6">
    <div className="max-w-md w-full rounded-2xl border border-red-200 bg-white p-6 text-center shadow-[0_10px_26px_rgba(16,24,40,0.08)] dark:border-red-900 dark:bg-gray-950">
      <h2 className="text-xl font-bold text-red-600 dark:text-red-400">Access Denied</h2>
      <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
        You do not have permission to access this page.
      </p>
    </div>
  </div>
);

const clearAuthStorage = () => {
  try {
    localStorage.removeItem("authUser");
    localStorage.removeItem("isAdminUser");
    localStorage.removeItem("userEmail");
    localStorage.removeItem("userRole");
    localStorage.removeItem("userId");
  } catch {
    // ignore storage errors
  }
};

const getAuthState = (): AuthState => {
  try {
    const userRaw = localStorage.getItem("authUser");
    const user = userRaw ? (JSON.parse(userRaw) as AuthUser) : null;
    const roleName = String(user?.role_name || "").toLowerCase();

    return {
      user,
      isAuthenticated: Boolean(user?.id || user?.email),
      isAdmin: roleName === "admin",
    };
  } catch {
    return {
      user: null,
      isAuthenticated: false,
      isAdmin: false,
    };
  }
};

function App() {
  const { isDarkMode, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [compareCompanyView, setCompareCompanyView] = useState<boolean>(() => {
    try {
      return localStorage.getItem("compareCompanyView") === "true";
    } catch {
      return false;
    }
  });

  const handleTileClick = (tile?: TileSelection | null) => {
    if (tile?.id === 1) {
      setCompareCompanyView(false);
      navigate("/manage-kpis");
      return;
    }

    if (tile?.id === 3) {
      setCompareCompanyView(true);
      navigate("/compare-company");
      return;
    }

    setCompareCompanyView(false);

    if (tile?.id === 4) {
      navigate("/contribute-data");
      return;
    }

    navigate("/benchmark");
  };

  const handleUsageTileClick = async (tile?: TileSelection | null) => {
    if (tile?.id === 1) {
      setCompareCompanyView(false);
      navigate("/options");
      return;
    }

    if (tile?.id === 2) {
      setCompareCompanyView(true);
      localStorage.removeItem(SELECTED_ACCOUNTS_KEY);
      navigate("/accounts");
      return;
    }

    if (tile?.id === 3) {
      setCompareCompanyView(false);
      navigate("/contribute-data");
    }
  };

  const handleCompanyCompareClick = () => {
    setCompareCompanyView(true);
    navigate("/benchmark");
  };

  const handleStandaloneKpiContinue = () => {
    navigate("/benchmark");
  };

  const handleStandaloneKpiSelectionChange = (_selection: SelectionChangePayload) => {
    // This route only needs the selector screen; the full benchmark flow wires the shared selection state.
  };

  const authState = getAuthState();
  const { isAuthenticated, isAdmin } = authState;
  const isAuthPage = location.pathname === "/login"
    || location.pathname === "/auth/callback"
    || location.pathname === "/auth/pending";

  axios.defaults.withCredentials = true;

  useEffect(() => {
    if (isDarkMode) {
      document.body.classList.add("dark-theme");
    } else {
      document.body.classList.remove("dark-theme");
    }
  }, [isDarkMode]);

  useEffect(() => {
    try {
      localStorage.setItem("compareCompanyView", String(compareCompanyView));
    } catch {
      // ignore storage errors
    }
  }, [compareCompanyView]);

  useEffect(() => {
    if (location.pathname !== "/") {
      return;
    }

    try {
      localStorage.removeItem("parametersSelections");
      localStorage.removeItem("kpiSelectorsSelections");
    } catch {
      // ignore storage errors
    }
  }, [location.pathname]);

  useEffect(() => {
    const interceptorId = axios.interceptors.response.use(
      (response) => response,
      (error: any) => {
        if (error?.response?.status === 401) {
          clearAuthStorage();
          const isAuthRoute = ["/login", "/auth/callback", "/auth/pending"].includes(window.location.pathname);
          if (!isAuthRoute) {
            navigate("/login", { replace: true });
          }
        }
        return Promise.reject(error);
      },
    );

    return () => {
      axios.interceptors.response.eject(interceptorId);
    };
  }, [navigate]);

useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    const loadSharedFirms = async () => {
      try {
        const roleName = localStorage.getItem("userRole") || "";
        const response = await axios.get(
          `${ACCOUNT_OPTIONS_API_BASE_URL}?role_name=${encodeURIComponent(roleName)}`,
          { withCredentials: true },
        );

        const sharedFirms = Array.isArray(response.data?.shared_firms)
          ? response.data.shared_firms.filter((item: unknown) => typeof item === "string" && item.trim())
          : [];

        localStorage.setItem(SELECTED_ACCOUNTS_KEY, JSON.stringify(sharedFirms));
      } catch (error) {
        console.error("[App.tsx] Failed to load shared_firms on app init:", error);
      }
    };

    loadSharedFirms();
  }, [isAuthenticated]);

  return (
    <div className="min-h-screen bg-white dark:bg-black">
      <header className="relative bg-white dark:bg-black shadow-[0_2px_16px_rgba(0,0,0,0.08)] dark:shadow-[0_4px_24px_rgba(0,0,0,0.35)] border-b-2 border-emerald-600/60">
        <div className="pointer-events-none absolute -top-8 -left-10 w-48 h-48 rounded-full bg-emerald-500/10 blur-3xl" />
        <div className="pointer-events-none absolute -top-6 right-10 w-40 h-40 rounded-full bg-sky-500/10 blur-2xl" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex flex-col lg:flex-row lg:justify-between gap-4 relative">
          <div className="flex items-center gap-4 sm:gap-8">
            {location.pathname !== "/usage" && !isAuthPage && (
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-gray-300 dark:border-white/20 bg-gray-100 dark:bg-white/10 text-gray-700 dark:text-white transition-colors hover:bg-gray-200 dark:hover:bg-white/20"
                aria-label="Go back"
                title="Go back"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="m15 18-6-6 6-6" />
                </svg>
              </button>
            )}
            <div className="flex items-center">
              <span
                className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white cursor-pointer"
                onClick={() => {
                  setCompareCompanyView(false);
                  navigate("/usage");
                }}
              >
                Deloitte<span className="text-emerald-600 dark:text-emerald-400">.</span>
              </span>
            </div>
            <div>
              <h1 className="text-base sm:text-xl font-bold text-gray-800 dark:text-white/90">
                Private Equity Insights Platform
              </h1>
            </div>
          </div>
          {!isAuthPage && (
            <div className="flex flex-col sm:flex-row sm:flex-wrap items-start sm:items-center gap-9 sm:gap-3">
              <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
                <div className="flex items-center gap-2">
                  <button
                    className="px-3 sm:px-4 py-1.5 text-xs sm:text-sm whitespace-nowrap rounded-lg font-medium transition-colors bg-gray-100 dark:bg-white/10 text-gray-700 dark:text-white border border-gray-300 dark:border-white/20 hover:bg-gray-200 dark:hover:bg-white/20"
                    onClick={() => {
                      setCompareCompanyView(false);
                      navigate("/help");
                    }}
                  >
                    Help/FAQs
                  </button>
                  <button
                    className="px-3 sm:px-4 py-1.5 text-xs sm:text-sm whitespace-nowrap rounded-lg font-medium transition-colors bg-gray-100 dark:bg-white/10 text-gray-700 dark:text-white border border-gray-300 dark:border-white/20 hover:bg-gray-200 dark:hover:bg-white/20"
                    onClick={() => {
                      setCompareCompanyView(false);
                      navigate("/feedback");
                    }}
                  >
                    Feedback
                  </button>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-1.5">
                <button
                  className="p-2 rounded-lg bg-gray-100 dark:bg-white/10 border border-gray-300 dark:border-white/20 hover:bg-gray-200 dark:hover:bg-white/20 transition-colors text-gray-700 dark:text-white"
                  onClick={toggleTheme}
                  aria-label="Toggle theme"
                  title="Toggle theme"
                >
                  {isDarkMode ? (
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="5"></circle>
                      <line x1="12" y1="1" x2="12" y2="3"></line>
                      <line x1="12" y1="21" x2="12" y2="23"></line>
                      <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line>
                      <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line>
                      <line x1="1" y1="12" x2="3" y2="12"></line>
                      <line x1="21" y1="12" x2="23" y2="12"></line>
                      <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line>
                      <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line>
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path>
                    </svg>
                  )}
                </button>
                <div className="flex items-center gap-1">
                  <UserMenu />
                </div>
              </div>
            </div>
          )}
        </div>
      </header>

      <Routes>
        <Route
          path="/"
          element={
            isAuthenticated ? (
              <UsageTiles onTileClick={handleUsageTileClick} />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />

        <Route
          path="/options"
          element={
            isAuthenticated ? (
              <TileContainer onClick={handleTileClick} />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />

        <Route path="/accounts" element={isAuthenticated ? <AccountsList /> : <Navigate to="/login" replace />} />

        <Route
          path="/onboard"
          element={isAuthenticated ? <DocumentChat /> : <Navigate to="/login" replace />}
        />
        <Route
          path="/benchmark"
          element={
            isAuthenticated ? (
              <UnifiedBenchmarkFlow compareCompanyView={compareCompanyView} />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/kpi-selector"
          element={
            isAuthenticated ? (
              <KpiSelectors
                compareCompanyView={compareCompanyView}
                onContinue={handleStandaloneKpiContinue}
                onSelectionChange={handleStandaloneKpiSelectionChange}
              />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/compare-company"
          element={
            isAuthenticated ? (
              <CompareCompany onClick={handleCompanyCompareClick} />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/manage-kpis"
          element={isAuthenticated ? <KpiUpload /> : <Navigate to="/login" replace />}
        />
        <Route
          path="/contribute-data"
          element={isAuthenticated ? <ContributeDataTiles /> : <Navigate to="/login" replace />}
        />
        <Route
          path="/contribute-standardized-data"
          element={isAuthenticated ? <ContributeDataPage /> : <Navigate to="/login" replace />}
        />
        <Route
          path="/contribute-raw-data"
          element={isAuthenticated ? <ContributeRawDataPage /> : <Navigate to="/login" replace />}
        />
        <Route
          path="/benchmarking-summary"
          element={
            isAuthenticated ? (
              <BenchMarkingSummary compareCompanyView={compareCompanyView} />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/company-benchmarking-summary"
          element={
            isAuthenticated ? (
              <CompanyBenchMarkingSummary compareCompanyView={compareCompanyView} />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/help"
          element={isAuthenticated ? <Help /> : <Navigate to="/login" replace />}
        />
        <Route
          path="/feedback"
          element={isAuthenticated ? <Feedback /> : <Navigate to="/login" replace />}
        />
        <Route
          path="/login"
          element={isAuthenticated ? <Navigate to="/" replace /> : <LoginPage />}
        />
        <Route
          path="/auth/callback"
          element={isAuthenticated ? <Navigate to="/" replace /> : <LoginPage />}
        />
        <Route
          path="/auth/pending"
          element={isAuthenticated ? <Navigate to="/" replace /> : <LoginPage />}
        />
        <Route
          path="/user-management"
          element={
            !isAuthenticated ? (
              <Navigate to="/login" replace />
            ) : isAdmin ? (
              <UserManagement />
            ) : (
              ACCESS_DENIED
            )
          }
        />
        <Route
          path="/role-management"
          element={
            !isAuthenticated ? (
              <Navigate to="/login" replace />
            ) : isAdmin ? (
              <RoleManagement />
            ) : (
              ACCESS_DENIED
            )
          }
        />
        <Route
          path="/account-management"
          element={
            !isAuthenticated ? (
              <Navigate to="/login" replace />
            ) : isAdmin ? (
              <AccountManagement />
            ) : (
              ACCESS_DENIED
            )
          }
        />
        <Route
          path="*"
          element={<Navigate to={isAuthenticated ? "/" : "/login"} replace />}
        />
      </Routes>

      {!isAuthPage && isAuthenticated && <ChatButton />}
    </div>
  );
}

export default App;